import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { send } from "../api/auth";
import { console_ } from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import { money } from "../system/format";
import { batchState, railNote, type BatchRow } from "../system/pay";

// ---------------------------------------------------------------------------
// SWEEP S21b. ONE PAYMENT BATCH, LINE BY LINE.
//
// `GET /v1/payment-batches/:id` has answered since V23 and no screen asked it, so the
// per-line verdict — the thing that says WHICH payment did not arrive — was readable
// only with curl. The pay screen's batch table links here by reference.
// ---------------------------------------------------------------------------

type Reconciliation = {
  batch_id: string;
  reference: string;
  provider: string;
  /** S44. Sent by the detail route; the provider list below is the fallback. */
  provider_name?: string;
  currency: string;
  is_sandbox: boolean;
  is_superseded: boolean;
  line_count: number;
  expected_minor: string;
  settled_minor: string;
  settled_count: number;
  failed_count: number;
  unreported_count: number;
  mismatch_count: number;
  is_reconciled: boolean | null;
};

type Line = {
  line_id: string;
  seq: number;
  worker_id: string;
  worker_name: string;
  amount_minor: string;
  currency: string;
  provider_reference: string | null;
  status_label: string | null;
  reported_amount_minor: string | null;
  verdict_label: string;
  /** 0210: set when the line reimburses an expense claim rather than paying a calculation. */
  expense_claim_id?: string | null;
};

export function PaymentBatch() {
  const { id = "" } = useParams();
  const { apiBase } = useSession();
  const { locale, t, refusal } = useT();
  const [batch, setBatch] = useState<{ lines: Line[]; reconciliation: Reconciliation } | null>(null);
  const [providers, setProviders] = useState<{ id: string; name: string }[]>([]);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await send<{ lines?: Line[]; reconciliation?: Reconciliation; error?: { message?: string } }>(
          console_.paymentBatch(apiBase, id),
        );
        if (res.status !== 200 || !res.body.lines || !res.body.reconciliation) {
          setFailed(refusal(res.body.error) ?? t("This batch could not be read."));
          return;
        }
        setBatch({ lines: res.body.lines, reconciliation: res.body.reconciliation });
        const p = await send<{ providers?: { id: string; name: string }[] }>(console_.paymentProviders(apiBase));
        if (p.status === 200 && p.body.providers) setProviders(p.body.providers);
      } catch {
        setFailed(t("We could not reach Verbunk."));
      }
    })();
  }, [apiBase, id]);

  if (failed) {
    return (
      <div className="form-message" role="alert">
        <StatusChip tone="failed" label={t("Not read")} />
        <span>{failed}</span>
      </div>
    );
  }
  if (!batch) return <p className="muted">{t("Reading the batch…")}</p>;

  const r = batch.reconciliation;
  // The list row's shape, so the state and the sandbox warning read the same words here.
  const row: BatchRow = { ...r, id: r.batch_id, total_minor: r.expected_minor };
  const state = batchState(row, t);
  const rail = railNote(row, t);

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t("Payment batch {reference}", { reference: r.reference })}</h2>
        <p className="muted">{t("A batch is immutable once run. A mistake is corrected by a new batch that supersedes it.")}</p>
      </div>
      <dl className="facts">
        <dt>{t("Rail")}</dt>
        <dd>
          {r.provider_name ?? providers.find((p) => p.id === r.provider)?.name ?? r.provider}
          {rail ? (
            <div>
              <StatusChip tone={rail.tone} label={rail.label} />
            </div>
          ) : null}
        </dd>
        <dt>{t("State")}</dt>
        <dd>
          <StatusChip tone={state.tone} label={state.label} />
        </dd>
        <dt>{t("Sent")}</dt>
        <dd>
          {t("{amount} in {n} payment(s)", { amount: money(r.expected_minor, r.currency), n: r.line_count })}
        </dd>
        <dt>{t("Settled")}</dt>
        <dd>
          {t("{amount} in {n} payment(s)", { amount: money(r.settled_minor, r.currency), n: r.settled_count })}
        </dd>
      </dl>

      <table className="data-table">
        <thead>
          <tr>
            <th scope="col" className="numeric">#</th>
            <th scope="col">{t("Person")}</th>
            <th scope="col" className="numeric">{t("Sent")}</th>
            <th scope="col">{t("Provider reference")}</th>
            <th scope="col">{t("Provider said")}</th>
            <th scope="col" className="numeric">{t("Reported")}</th>
            <th scope="col">{t("Verdict")}</th>
          </tr>
        </thead>
        <tbody>
          {batch.lines.map((l) => (
            <tr key={l.line_id}>
              <td className="numeric">{l.seq}</td>
              <td>
                {l.worker_name}
                {l.expense_claim_id ? (
                  <>
                    <br />
                    <span className="muted">{t("Reimbursement, not wages")}</span>
                  </>
                ) : null}
              </td>
              <td className="numeric">{money(l.amount_minor, l.currency)}</td>
              <td>{l.provider_reference ?? "—"}</td>
              <td>{l.status_label === null ? "—" : translateLabel(locale, l.status_label)}</td>
              <td className="numeric">
                {l.reported_amount_minor === null ? "—" : money(l.reported_amount_minor, l.currency)}
              </td>
              <td>{translateLabel(locale, l.verdict_label)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
